/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupport.com)
 *
 * @description
 *
 * @created at Fri Sep 13 2024
 **/

import React, { useEffect, useRef, useState } from "react";

import { SidebarID } from "@/frameworks/core/commons";
import { useCreateWorkGroup, useSidebarState } from "@/frameworks/core/hooks";
import { Chip, TextField, useToast } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";

import * as S from "../emotion";

export default function KeywordField() {
	const tagRef = useRef<HTMLInputElement>(null);
	const toast = useToast();
	const [keyword, setKeyword] = useState("");
	const {
		keywords,
		handlers: { handleReset, handleAddKeyword, handleRemoveKeyword, handleRemoveAllKeyword }
	} = useCreateWorkGroup();
	const sidebarState = useSidebarState();
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setKeyword(event.target.value.replace(",", ""));
	};

	const handleAddChip = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (!keyword) return;

		if (keyword === " ") {
			handleReset(tagRef, () => setKeyword(""));
			return;
		}

		if (event.key === " " || event.key === "," || event.key === "Enter") {
			if (keywords.indexOf(keyword) !== -1) {
				toast.show({ type: "error", message: _T("이미 추가된 키워드 입니다.") });
			} else {
				handleAddKeyword(keyword);
			}

			handleReset(tagRef, () => setKeyword(""));
			return;
		}
	};

	const handleRemoveChip = (keyword: string) => {
		handleRemoveKeyword(keyword);
	};

	useEffect(() => {
		if (sidebarState.origin[SidebarID.CreateWorkGroup]?.isOpen === false) {
			handleReset(tagRef, () => {
				setKeyword("");
				handleRemoveAllKeyword();
			});
		}
	}, [sidebarState.origin[SidebarID.CreateWorkGroup]?.isOpen]);

	return (
		<S.FieldBox>
			<S.KeywordWrapper>
				<TextField.Tag
					ref={tagRef}
					label={_T("검색 키워드")}
					placeholder={_T("키워드 입력 후, 엔터를 누르면 추가됩니다.")}
					value={keyword}
					minHeight="40px"
					maxLength={20}
					maxHeight="132px"
					onChange={handleChange}
					onKeyUp={handleAddChip}
					tags={[
						keywords.length > 0 &&
							keywords.map((item) => (
								<Chip
									key={item}
									colorTheme="purple"
									data-keyword={item}
									handleClose={() => handleRemoveChip(item)}
									isEllipsisText
								>
									{item}
								</Chip>
							))
					]}
				/>
			</S.KeywordWrapper>
		</S.FieldBox>
	);
}
